import React, { useState } from "react";
import { Reveal, SectionHeading } from "../shared/Primitives";
import { Terminal as TerminalIcon, Sparkles, Cpu, Wrench, Activity, CornerDownRight, Play, CheckCircle2 } from "lucide-react";

export default function TerminalSection({ config }) {
    const [input, setInput] = useState("");
    const [history, setHistory] = useState([
        { type: "system", content: "Hariom OS [Version 2.4.0-prod] — Interactive Developer CLI initialized." },
        { type: "info", content: "Type 'help' or click any prompt pill below to explore system architecture and workflow." }
    ]);

    const COMMANDS = {
        help: [
            "AVAILABLE COMMANDS:",
            "  stack      -> Output core technologies & architectures",
            "  ai         -> Deep-dive into RAG & LLM agent implementations",
            "  qa         -> Test automation, API validation & database suites",
            "  workflow   -> Daily developer routine & productivity tools",
            "  easteregg  -> Unlock secret developer achievement",
            "  clear      -> Reset terminal console buffer"
        ],
        stack: [
            "[FRONTEND]: Next.js (App Router), React 19, TypeScript, TailwindCSS, Framer Motion",
            "[BACKEND]:  FastAPI, Python 3.12, Node.js, REST APIs, Celery Workers",
            "[DATABASE]: PostgreSQL, Qdrant Vector DB, Redis, MongoDB",
            "[CLOUD]:    Docker, Vercel, CI/CD automated test pipelines, Linux"
        ],
        ai: [
            "AI & RAG ARCHITECTURE ENGINE:",
            "✔ Vector Search: Qdrant embeddings with zero-hallucination semantic constraints.",
            "✔ Agentic Layer: SQL Agents and tool-calling LLMs grounded in strict hospital RBAC.",
            "✔ Document parsing: High-speed ATS CV matching and structured metadata retrieval."
        ],
        qa: [
            "ENTERPRISE QUALITY ASSURANCE PROTOCOL:",
            "✔ End-to-End Automation: Playwright deterministic regression suites.",
            "✔ API & DB Verification: Deep state consistency checks and boundary load testing.",
            "✔ Regression Target: ZERO production downtime across all critical deployments."
        ],
        workflow: [
            "DAILY PRODUCTION RHYTHM:",
            "08:00 AM -> Architectural review & CI/CD telemetry checks.",
            "10:00 AM -> Deep focus code sprints: RAG optimizations & FastAPI service scaling.",
            "02:00 PM -> Automated test suite generation & bug eradication cycles.",
            "05:00 PM -> Open source contributions & community peer code audits."
        ],
        easteregg: [
            "🤖 SECRET UNLOCKED! [100x ENGINEER REVEALED]",
            "You discovered the system easter egg! Hariom brings production velocity, enterprise quality, and cutting-edge AI architecture directly to your product.",
            "⚡ Recommended Action: Click 'Contact' or email Hariompatel33699@gmail.com to hire!"
        ]
    };

    const handleRunCommand = (cmd) => {
        const cleanCmd = cmd.toLowerCase().trim();
        if (cleanCmd === "clear") {
            setHistory([{ type: "info", content: "Console buffer cleared. Type 'help' to begin." }]);
            return;
        }

        const response = COMMANDS[cleanCmd] || [
            `Command not recognized: '${cmd}'. Type 'help' to see available terminal instructions.`
        ];

        setHistory((prev) => [
            ...prev,
            { type: "prompt", content: `$ ${cmd}` },
            ...response.map((line) => ({ type: cleanCmd === "easteregg" ? "highlight" : "output", content: line }))
        ]);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!input) return;
        handleRunCommand(input);
        setInput("");
    };

    const pills = ["help", "stack", "ai", "qa", "workflow", "easteregg", "clear"];

    return (
        <section className="max-w-6xl mx-auto px-5 py-24 md:py-32">
            <SectionHeading 
                id="workspace" 
                index="07 / Workspace" 
                subtitle="Interactive Terminal & Stack" 
                title="Under the hood of my daily development engine." 
            />

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                {/* Interactive CLI Terminal */}
                <Reveal className="lg:col-span-7">
                    <div className="glass rounded-3xl overflow-hidden border border-border/70 shadow-2xl flex flex-col h-[520px] bg-card/80">
                        {/* Terminal Header Bar */}
                        <div className="bg-muted/60 px-5 py-3.5 border-b border-border/50 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <div className="w-3 h-3 rounded-full bg-red-500/80" />
                                <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
                                <div className="w-3 h-3 rounded-full bg-green-500/80" />
                            </div>
                            <div className="font-mono text-xs text-muted-foreground flex items-center gap-2 font-medium">
                                <TerminalIcon className="w-3.5 h-3.5 text-primary" /> hariom@os:~ (bash)
                            </div>
                            <div className="w-16" />
                        </div>

                        {/* Terminal Log Screen */}
                        <div className="p-5 overflow-y-auto flex-1 font-mono text-xs sm:text-sm space-y-2 text-foreground/90 selection:bg-primary/30">
                            {history.map((item, idx) => (
                                <div 
                                    key={idx} 
                                    className={`${
                                        item.type === "prompt" ? "text-primary font-bold mt-3" :
                                        item.type === "system" ? "text-accent font-semibold" :
                                        item.type === "highlight" ? "text-amber-400 font-medium bg-amber-950/20 p-2 rounded-lg border border-amber-500/30" :
                                        "text-muted-foreground pl-2"
                                    }`}
                                >
                                    {item.content}
                                </div>
                            ))}
                        </div>

                        {/* Clickable Quick Command Chips */}
                        <div className="px-5 py-2.5 bg-secondary/30 border-t border-border/40 flex flex-wrap gap-2 items-center">
                            <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground mr-1">Quick Run:</span>
                            {pills.map((p) => (
                                <button
                                    key={p}
                                    onClick={() => handleRunCommand(p)}
                                    className="px-2.5 py-1 rounded-md text-xs font-mono bg-secondary/80 hover:bg-primary hover:text-primary-foreground transition-colors border border-border/40 flex items-center gap-1"
                                >
                                    <Play className="w-2.5 h-2.5" /> {p}
                                </button>
                            ))}
                        </div>

                        {/* Input line */}
                        <form onSubmit={handleSubmit} className="px-5 py-3 border-t border-border/50 bg-background/50 flex items-center gap-2">
                            <span className="text-primary font-mono font-bold flex items-center gap-1">
                                <CornerDownRight className="w-4 h-4" /> $
                            </span>
                            <input 
                                type="text" 
                                value={input} 
                                onChange={(e) => setInput(e.target.value)}
                                placeholder="Type a command here (e.g. 'stack' or 'help')..." 
                                className="flex-1 bg-transparent border-none outline-none font-mono text-xs sm:text-sm text-foreground placeholder:text-muted-foreground/50"
                            />
                            <button type="submit" className="text-xs font-mono px-3 py-1 rounded bg-primary/20 text-primary hover:bg-primary hover:text-white transition-colors">
                                Run
                            </button>
                        </form>
                    </div>
                </Reveal>

                {/* AI Stack, Tech Radar & Telemetry Side Panel */}
                <div className="lg:col-span-5 flex flex-col gap-5 justify-between">
                    {/* AI Stack */}
                    <Reveal delay={0.1}>
                        <div className="glass rounded-3xl p-6 border border-border/60 relative overflow-hidden group">
                            <div className="absolute -right-10 -top-10 w-32 h-32 rounded-full bg-primary/10 blur-2xl group-hover:bg-primary/20 transition-all" />
                            <div className="flex items-center gap-2 text-primary mb-3 font-mono text-xs uppercase tracking-widest font-semibold">
                                <Cpu className="w-4 h-4" /> AI Stack & Tech Radar
                            </div>
                            <h4 className="font-display font-bold text-lg mb-3">Grounded LLM & RAG Architectures</h4>
                            <div className="flex flex-wrap gap-2">
                                {["Vector Embeddings", "Qdrant DB", "LangGraph", "SQL Agents", "Semantic Guardrails", "OpenAI / Claude"].map((t) => (
                                    <span key={t} className="px-3 py-1 rounded-full text-xs bg-primary/10 text-primary border border-primary/20 font-medium">
                                        {t}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </Reveal>

                    {/* Daily Workflow */}
                    <Reveal delay={0.15}>
                        <div className="glass rounded-3xl p-6 border border-border/60 relative overflow-hidden group">
                            <div className="flex items-center gap-2 text-accent mb-3 font-mono text-xs uppercase tracking-widest font-semibold">
                                <Wrench className="w-4 h-4" /> Daily Workflow & Tooling
                            </div>
                            <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                                Hyper-optimized engineering workspace built for velocity, strict testing assertions, and zero regressions.
                            </p>
                            <div className="grid grid-cols-2 gap-2.5 text-xs text-foreground/90 font-mono">
                                <span className="flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-green-500" /> Playwright Suites</span>
                                <span className="flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-green-500" /> Vercel CI/CD</span>
                                <span className="flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-green-500" /> Docker Containers</span>
                                <span className="flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-green-500" /> Raycast & Arc</span>
                            </div>
                        </div>
                    </Reveal>

                    {/* Live Visitor Counter & Telemetry */}
                    <Reveal delay={0.2}>
                        <div className="glass rounded-3xl p-6 border border-border/60 bg-gradient-to-r from-secondary/50 via-card/60 to-secondary/50 flex items-center justify-between">
                            <div className="flex items-center gap-3.5">
                                <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-green-500/20 to-emerald-500/20 border border-green-500/30 flex items-center justify-center text-green-400 font-bold">
                                    <Activity className="w-5 h-5 animate-pulse text-green-500" />
                                </div>
                                <div>
                                    <div className="flex items-center gap-2">
                                        <span className="font-display font-bold text-xl tabular-nums text-foreground">2,841</span>
                                        <span className="relative flex h-2 w-2">
                                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                                            <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" />
                                        </span>
                                    </div>
                                    <div className="text-xs text-muted-foreground font-mono">Live Developer Telemetry · 100% Uptime</div>
                                </div>
                            </div>
                            <div className="hidden sm:block text-right">
                                <span className="text-[11px] font-mono uppercase bg-green-500/10 text-green-400 px-2.5 py-1 rounded-full border border-green-500/20">
                                    Verified Prod
                                </span>
                            </div>
                        </div>
                    </Reveal>
                </div>
            </div>
        </section>
    );
}
